import {
  Component,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { GroupKeyDto } from '@models/clash/group-key-dto';
import { GenderPipe } from '@shared/pipes/naipe.pipe';
import { ClashService } from '@shared/services/clash.service';
import { InscricaoChaveDto } from '@shared/services/competition/group-key.service';
import { StepParameterizationService } from '@shared/services/competition/step-parametrization.service';
import { BehaviorSubject, forkJoin, map, of } from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';

@Component({
  selector: 'sigecom-key-grid',
  host: { class: 'block' },
  templateUrl: './key-grid.component.html',
})
export class KeyGridComponent extends BaseComponent implements OnChanges {
  @Input() petNrId!: number;
  @Input() petNrIds: number[] = [];
  @Input() qtdCabecaChavesPorChave = 0;

  readonly #clashService = inject(ClashService);
  readonly #stepParametrizationsService = inject(StepParameterizationService);

  private readonly groupsSubject = new BehaviorSubject<ISorteioKeyGroup[]>([]);
  public readonly groups$ = this.groupsSubject.asObservable();

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['petNrId'] || changes['petNrIds']) && this.hasAnyPetNrId()) {
      this.loadGroups();
    }
  }

  private hasAnyPetNrId(): boolean {
    const ids = this.petNrIds?.length
      ? this.petNrIds
      : this.petNrId
        ? [this.petNrId]
        : [];
    return ids.length > 0;
  }

  private loadGroups(): void {
    const petNrIds = this.petNrIds?.length
      ? this.petNrIds
      : this.petNrId
        ? [this.petNrId]
        : [];

    if (!petNrIds.length) {
      this.groupsSubject.next([]);
      return;
    }

    forkJoin(
      petNrIds.map((petNrId) =>
        forkJoin({
          petNrId: of(petNrId),
          keys: this.#clashService
            .getClashs({ petNrId }, { page: 0, size: 999 })
            .pipe(map((res) => res.content ?? [])),
          description: this.#stepParametrizationsService
            .getSimpleStepParametrization$({ petNrId }, { page: 0, size: 100 })
            .pipe(
              map((step) => {
                const eveTxNome = step.content?.at(0)?.eveTxNome;
                const genderPipe = new GenderPipe();
                const catTxSexo = genderPipe.transform(
                  step.content?.at(0)?.catTxSexo,
                );
                return `${eveTxNome} - ${catTxSexo}`;
              }),
            ),
        }),
      ),
    )
      .pipe(
        map((results) =>
          results.map(({ petNrId, keys, description }) => ({
            petNrId,
            description,
            minimized: false,
            keys: keys
              .filter(
                (key, index, list) =>
                  list.findIndex((item) => item.cheNrId === key.cheNrId) ===
                    index,
              )
              .sort((a, b) => this.compareKeyNames(a.cheTxNome, b.cheTxNome))
              .map((key) => ({
                ...key,
                inscricoesChave: key.inscricoesChave?.map((inc) => ({
                  ...inc,
                  estTxSigla: this.getDisplaySiglaStateSede(inc),
                })),
              })),
          })),
        ),
        this.takeUntilDestroy(),
      )
      .subscribe({
        next: (groups) => this.groupsSubject.next(groups),
      });
  }

  getSlots(item: GroupKeyDto): { isHead: boolean }[] {
    const total = item.cheNrQtdInc || 0;
    const maxHeads = this.qtdCabecaChavesPorChave || 0;
    return Array.from({ length: total }, (_, index) => ({
      isHead: index < maxHeads,
    }));
  }

  trackClashById(_: number, item: GroupKeyDto): number {
    return item.cheNrId;
  }

  toggleGroupMinimize(group: ISorteioKeyGroup): void {
    group.minimized = !group.minimized;
  }

  trackGroupByPetNrId(_: number, group: ISorteioKeyGroup): number {
    return group.petNrId;
  }

  private getDisplaySiglaStateSede(inc: InscricaoChaveDto): string {
    if (inc.ichBlSede) {
      return '1 - ' + inc.estTxSigla;
    }
    return inc.estTxSigla;
  }

  private compareKeyNames(firstName?: string, secondName?: string): number {
    return (firstName ?? '').trim().localeCompare(
      (secondName ?? '').trim(),
      'pt-BR',
      { sensitivity: 'base', numeric: true },
    );
  }
}

export interface ISorteioKeyGroup {
  petNrId: number;
  description: string;
  keys: GroupKeyDto[];
  minimized: boolean;
}
